import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Lock, User } from 'lucide-react';
import { selectSessionAdvisor } from '@client/model/selectors';
import { redtailApi } from '@client/api/integrations';
import { Redtail } from '@/api/types';

export function AdvisorIntegrationsRedtailAuth() {
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const authMutation = useMutation({
    mutationFn: (data: Redtail.AuthRequest) => redtailApi.auth.login(data),
    onSuccess: () => {
      navigate(`/advisors/${advisor.advisorId}/integrations/redtail/contacts`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    authMutation.mutate({ username, password });
  };

  const handleBack = () => {
    navigate(`/advisors/${advisor.advisorId}/integrations`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-2xl">
        {/* Back button */}
        <button
          onClick={handleBack}
          className="mb-6 flex items-center gap-2 text-gray-700 transition-colors hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Integrations
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Connect to Redtail CRM</h1>
          <p className="mt-2 text-gray-700">
            Enter your Redtail credentials to connect your account
          </p>
        </div>

        {/* Form Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
          <form
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Username Field */}
            <div>
              <label
                htmlFor="username"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Redtail Username
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <User className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Enter your Redtail username"
                  required
                  className="focus:border-primary focus:ring-primary block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-1"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Redtail Password
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Lock className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your Redtail password"
                  required
                  className="focus:border-primary focus:ring-primary block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-1"
                />
              </div>
            </div>

            {/* Error Message */}
            {authMutation.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-800">
                  {authMutation.error instanceof Error
                    ? authMutation.error.message
                    : 'Failed to connect to Redtail'}
                </p>
              </div>
            )}

            {/* Info Box */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Your Redtail credentials are securely stored and only used to
                sync your contacts. We never share your credentials with third parties.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={authMutation.isPending}
              className="bg-primary hover:bg-primary-600 w-full rounded-lg px-4 py-3 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {authMutation.isPending ? 'Connecting...' : 'Connect to Redtail'}
            </button>
          </form>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-700">
            Don't have a Redtail account?{' '}
            <a
              href="https://www.redtailtechnology.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-600 font-medium"
            >
              Learn more about Redtail
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
